import React, { useEffect, useRef, useState, memo } from "react";
import { Box, Divider, Typography, TextField, IconButton, Alert } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CustomTable from "../../../../../../../shared/components/table/custom-table";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { useParams } from "react-router-dom";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { LoaderComponent } from "../../../../../../../shared/components/loaders/page-loader";

interface ProductShipmentResponse {
  tcProductNo: string;
}

interface RawMaterialDetail {
  tcProductCategoryCode: string;
  tcProductDetailCode: string;
  rawMaterialCode: string;
  rawMaterialPercentage: number;
  calculatedWeight: string;
}

interface IntermediateLedgerRmData {
  tcProductNo: string;
  tcProductWeightDetails: Array<{
    tcProductCategoryCode: string;
    tcProductDetailCode: string;
    rawMaterialCode: string;
    rawMaterialPercentage: number;
  }>;
}

interface IntermediateLedgerWeightData {
  tcProductNo: string;
  netShippingWeight: number;
}

interface ComponentInfoProps {
  formRef: React.Ref<any>;
  componentData: any;
  setComponentData: (data: any) => void;
  selectedArticles?: string[];
  incomingTcId?: string; 
  incomingAtcId?: string; 
  incomingLedgerTransactionId? : string;
}

const RenderRawMaterialPanel = memo(({ row, rawMaterialDetails, loading }: { 
  row: { original: ProductShipmentResponse }, 
  rawMaterialDetails: RawMaterialDetail[] | undefined,
  loading?: boolean,
}) => {
  if (loading) {
    // Show only the shared LoaderComponent while waiting for weight/raw data
    return (
      <Box sx={{ width: "100%", p: 2, display: 'flex', justifyContent: 'center' }}>
        <LoaderComponent />
      </Box>
    );
  }

  if (!rawMaterialDetails || rawMaterialDetails.length === 0) {
    return (
      <Box sx={{ width: "100%", p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          No raw material data available.
        </Typography>
      </Box>
    );
  }

  const rawMaterialColumns = [
    { accessorKey: "tcProductCategoryCode", header: "Product Category", minSize: 60, maxSize: 150 },
    { accessorKey: "tcProductDetailCode", header: "Product Detail", minSize: 60, maxSize: 150 },
    { accessorKey: "rawMaterialCode", header: "Raw Material", minSize: 60, maxSize: 150 },
    { accessorKey: "rawMaterialPercentage", header: "Raw Material %", minSize: 60, maxSize: 150 },
    { accessorKey: "calculatedWeight", header: "Raw Material Weight", minSize: 60, maxSize: 150 },
  ];

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <CustomTable
        columns={rawMaterialColumns}
        data={rawMaterialDetails}
        enableTopToolbar={false}
        enableBottomToolbar={false}
      />
    </Box>
  );
});

const ProductNetWeight: React.FC<ComponentInfoProps> = ({ 
  formRef, 
  componentData, 
  setComponentData, 
  selectedArticles, 
  incomingTcId, 
  incomingAtcId,
  incomingLedgerTransactionId
}) => {
  const [products, setProducts] = useState<ProductShipmentResponse[]>([]);
  const [netWeights, setNetWeights] = useState<{ [key: string]: string }>({});
  const [expanded, setExpanded] = useState<string[]>([]);
  const [rawMaterialData, setRawMaterialData] = useState<{ [key: string]: Omit<RawMaterialDetail, 'calculatedWeight'>[] }>({});
  const [rawMaterialLoading, setRawMaterialLoading] = useState<{ [key: string]: boolean }>({});
  const [maxWeights, setMaxWeights] = useState<{ [key: string]: number }>({});
  const [weightErrors, setWeightErrors] = useState<{ [key: string]: boolean }>({});
  const [isLoading, setIsLoading] = useState(false);
  
  const { TE_ID } = useParams();
  const { executeGet } = useApi();

  const effectiveTcId = incomingTcId || incomingAtcId || incomingLedgerTransactionId;

  useEffect(() => {
    if (componentData?.netWeights && Object.keys(componentData.netWeights).length > 0) {
      const allKeys = Object.keys(componentData.netWeights);
      const filteredKeys = Array.isArray(selectedArticles) && selectedArticles.length > 0
        ? allKeys.filter((k) => selectedArticles.includes(k))
        : allKeys;

      const initialWeights: { [key: string]: string } = {};
      filteredKeys.forEach((key) => {
        const value = componentData.netWeights[key];
        if (value !== undefined && value !== null) {
          initialWeights[key] = String(value);
        }
      });
      setNetWeights(initialWeights);

      if (products.length === 0) {
        const productsFromWeights = filteredKeys.map((key) => ({ tcProductNo: key }));
        setProducts(productsFromWeights);
      }
    }
  }, [componentData?.netWeights, JSON.stringify(selectedArticles)]);

  useEffect(() => {
    if (componentData?.rawMaterials && Object.keys(componentData.rawMaterials).length > 0) {
      
      if (Array.isArray(selectedArticles) && selectedArticles.length > 0) {
        const filtered: { [key: string]: Omit<RawMaterialDetail, 'calculatedWeight'>[] } = {};
        Object.entries(componentData.rawMaterials).forEach(([key, value]) => {
          if (selectedArticles.includes(key)) {
            filtered[key] = value as any;
          }
        });
        setRawMaterialData(filtered);
      } else {
        setRawMaterialData(componentData.rawMaterials);
      }
    }
  }, [componentData?.rawMaterials, JSON.stringify(selectedArticles)]);


  useEffect(() => {
    if (!Array.isArray(selectedArticles)) return;

    setNetWeights((prev) => {
      const entries = Object.entries(prev).filter(([k]) => selectedArticles.includes(k));
      return Object.fromEntries(entries);
    });

    
    setRawMaterialData((prev) => {
      const out: typeof prev = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (selectedArticles.includes(k)) out[k] = v;
      });
      return out;
    });

    
    setMaxWeights((prev) => {
      const entries = Object.entries(prev).filter(([k]) => selectedArticles.includes(k));
      return Object.fromEntries(entries);
    });

    setExpanded((prev) => prev.filter((p) => selectedArticles.includes(p)));

    
    setProducts((prev) => prev.filter((p) => selectedArticles.includes(p.tcProductNo)));
  }, [JSON.stringify(selectedArticles)]);

  const fetchProducts = async (selected?: string[]) => {
    if (!effectiveTcId) return;
    
    setIsLoading(true);
    try {
      let productData: ProductShipmentResponse[] = [];
  
      if (selected && selected.length > 0) {
        productData = selected.map((art) => ({
          tcProductNo: art,
        }));
      } else {
        const response = await executeGet(TCAppConfig.GET_LIST_PRODUCT_INFO_V4, { tcId: effectiveTcId });
        productData = (response?.data?.response || []).map((item: any) => ({
          tcProductNo: item.tcProductArticleNo || item.tcProductNo,
        }));
      }
  
      setProducts(productData);
  
      // Fetch weights/RM for the (now non-empty) productData.
      const weightPromises = productData.map((product) => fetchMaxWeight(product.tcProductNo));
      const rmPromises = productData.map((product) => fetchRawMaterialData(product.tcProductNo));
  
      await Promise.all([...weightPromises, ...rmPromises]);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRawMaterialData = async (productNo: string) => {
    if (!effectiveTcId) {
      console.warn(`fetchRawMaterialData: effectiveTcId missing for product ${productNo}`);
      return;
    }
    
    // If data already present, skip fetching
    if (rawMaterialData[productNo] && rawMaterialData[productNo].length > 0) {
      return;
    }
    try {
      const apiResponse = await executeGet(TCAppConfig.GET_INTERMEDIATE_DATA_V4, { 
        tcId: effectiveTcId, 
        productNo 
      });
      
      const response: IntermediateLedgerRmData | undefined = apiResponse?.data;
      
      if (response && response.tcProductWeightDetails && response.tcProductWeightDetails.length > 0) {
        const details = response.tcProductWeightDetails.map((item) => ({
          tcProductCategoryCode: item.tcProductCategoryCode,
          tcProductDetailCode: item.tcProductDetailCode,
          rawMaterialCode: item.rawMaterialCode,
          rawMaterialPercentage: item.rawMaterialPercentage,
        }));
        setRawMaterialData((prev) => ({ ...prev, [productNo]: details }));
      } else {
        setRawMaterialData((prev) => ({ ...prev, [productNo]: [] }));
      }
    } catch (err) {
      console.error(`Failed to fetch raw material data for ${productNo}:`, err);
      setRawMaterialData((prev) => ({ ...prev, [productNo]: [] }));
    }
  };

  const fetchMaxWeight = async (productNo: string) => {
    if (!effectiveTcId) return;
    
    // Check if we already have this data
    if (maxWeights[productNo] !== undefined) {
      return;
    }

    try {
      const apiResponse = await executeGet(TCAppConfig.GET_INTERMEDIATE_WEIGHT_V4, { 
        tcId: effectiveTcId, 
        productNo 
      });
      
      const response: IntermediateLedgerWeightData = apiResponse?.data;
      
      if (response && typeof response.netShippingWeight === 'number') {
        setMaxWeights((prev) => ({ ...prev, [productNo]: response.netShippingWeight }));
      } else {
        setMaxWeights((prev) => ({ ...prev, [productNo]: 0 }));
      }
    } catch (err) {
      console.error(`Failed to fetch max weight for ${productNo}:`, err);
      setMaxWeights((prev) => ({ ...prev, [productNo]: 0 }));
    }
  };

  // Fetch products when effectiveTcId or selectedArticles change
  useEffect(() => {
    if (effectiveTcId && selectedArticles && selectedArticles.length > 0) {
      fetchProducts(selectedArticles);
    }
  }, [effectiveTcId, JSON.stringify(selectedArticles)]);

  useEffect(() => {
    if (!Array.isArray(selectedArticles)) return;
  
    const newExpanded: string[] = [];
    selectedArticles.forEach(article => {
      if (rawMaterialData[article] && rawMaterialData[article].length > 0) {
        newExpanded.push(article);
      }
    });
  
    setExpanded(prev => {
      const merged = [...new Set([...prev, ...newExpanded])];
      return merged;
    });
  }, [rawMaterialData, JSON.stringify(selectedArticles)]);

  // Sync componentData back to parent
  useEffect(() => {
    const numericWeights: { [key: string]: number } = {};
    Object.entries(netWeights).forEach(([productNo, weight]) => {
      const numWeight = Number(weight);
      if (!isNaN(numWeight) && numWeight > 0) {
        numericWeights[productNo] = numWeight;
      }
    });
    const newData = {
      netWeights: numericWeights,
      rawMaterials: rawMaterialData,
      maxWeights,
      weightErrors,
    };

    if (JSON.stringify(componentData) !== JSON.stringify(newData)) {
      setComponentData(newData);
    }
  }, [netWeights, rawMaterialData]);

  const toggleExpand = async (productNo: string) => {
    const isCurrentlyExpanded = expanded.includes(productNo);
  
    if (!isCurrentlyExpanded) {
      try {
        await fetchMaxWeight(productNo);
  
        setExpanded((prev) => [...prev, productNo]);
        setRawMaterialLoading((prev) => ({ ...prev, [productNo]: true }));
  
        await fetchRawMaterialData(productNo);
        setRawMaterialLoading((prev) => ({ ...prev, [productNo]: false }));
      } catch (err) {
        console.error(`Error in toggleExpand for ${productNo}:`, err);
        setRawMaterialLoading((prev) => ({ ...prev, [productNo]: false }));
      }
    } else {
      setExpanded((prev) => prev.filter((p) => p !== productNo));
    }
  };

  const handleChange = (productNo: string, value: string) => {
    if (value !== "" && !/^\d*(?:\.\d{0,5})?$/.test(value)) {
      return;
    }
    const inputNum = Number(value);
    const maxWeight = maxWeights[productNo] || 0;
    // Only flag error when a valid certified maxWeight exists (> 0)
    const hasError = value !== "" && (!isNaN(inputNum) && (inputNum <= 0 || (maxWeight > 0 && inputNum > maxWeight)));
  
    setNetWeights((prev) => ({ ...prev, [productNo]: value }));
    setWeightErrors((prev) => ({ ...prev, [productNo]: hasError }));
  };

  if (isLoading) {
    return <LoaderComponent />;
  }
  if (products.length === 0 ) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          No products available. Please select incoming products in the previous step.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {products.map((product) => {
        const productNo = product.tcProductNo;
        const isExpanded = expanded.includes(productNo);
    
        const currentNetWeight = Number(netWeights[productNo]) || 0;

        const baseDetails = rawMaterialData[productNo] || [];
        const hasError = weightErrors[productNo];
        const maxWeight = maxWeights[productNo] || 0;

        return (
          <Box key={productNo}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1,
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                boxShadow: "0px 2px 1px -1px rgba(0,0,0,0.2)",
              }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                Incoming Product Article No.: {productNo}
              </Typography>
              <Typography variant="body2" sx={{ mr: 1 }}>
                  Product Net Weight Used:
                  <br />
                  {`Max: ${maxWeight.toFixed(2)}`}
                </Typography>
              <TextField
                variant="outlined"
                size="small"
                value={netWeights[productNo] || ""}
                onChange={(e) => handleChange(productNo, e.target.value)}
                sx={{ width: "100px", backgroundColor: "white" }}
                inputProps={{ type: "number", min: 0.00001, step: "0.00001" }}
                error={hasError}
              />
              <IconButton onClick={() => toggleExpand(productNo)} sx={{ ml: 1 }}>
                <ArrowDropDownIcon sx={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" }} />
              </IconButton>
            </Box>
       
            {hasError && (
              <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                {currentNetWeight <= 0
                  ? "Input net shipping weight must be greater than 0."
                  : `Input net shipping weight (${currentNetWeight.toFixed(2)}) exceeds the maximum allowed (${maxWeight.toFixed(2)}). Please adjust.`}
              </Alert>
            )}
            {isExpanded && (
              <RenderRawMaterialPanel 
                row={{ original: product }} 
                rawMaterialDetails={baseDetails.map((item) => ({
                  ...item,
                  calculatedWeight: ((item.rawMaterialPercentage / 100) * currentNetWeight).toFixed(2)
                }))}
                loading={!!rawMaterialLoading[productNo]}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ProductNetWeight;
